import { FilterOutlined, SortAscendingOutlined } from "@ant-design/icons"
import { Badge, Button, Flex, Radio, Skeleton } from "antd"
import { type CheckboxOptionType } from "antd/es/checkbox/Group"
import type { BaseOptionType } from "antd/lib/select"
import React, { useMemo, useState } from "react"

import {
  ACTIVE_STATUSES,
  INACTIVE_STATUSES
} from "../../constants/project-statuses.ts"
import {
  type FilterField,
  countActiveFilters
} from "../../redux/projects.constants.ts"
import type { ProjectFilters } from "../../redux/projects.types.ts"
import { ProjectListGridFiltersDrawer } from "./ProjectListGridFiltersDrawer.tsx"
import {
  ProjectListGridFiltersOrdering,
  type SortOption
} from "./ProjectListGridFiltersOrdering.tsx"

interface ProjectListGridFiltersProps {
  filters: ProjectFilters
  fetching: boolean
  filterOptions: Record<string, BaseOptionType[]>
  sortOptions?: SortOption[]
  filterFields?: FilterField[]
  onFiltersChange: (data: Partial<ProjectFilters>) => void
}

export function ProjectListGridFilters({
  filters,
  fetching,
  filterOptions,
  sortOptions = [],
  filterFields = [],
  onFiltersChange
}: ProjectListGridFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false)

  const project_status = filterOptions?.project_status

  const onFilterChange = (key: keyof ProjectFilters) => (value: unknown) => {
    onFiltersChange({ [key]: value || null })
  }

  const statusOptions = useMemo(() => {
    return project_status?.reduce<CheckboxOptionType[]>(
      (acc, el) => {
        if (INACTIVE_STATUSES.includes(el?.value))
          acc.push(el as CheckboxOptionType)
        return acc
      },
      [
        { label: "All", value: null },
        {
          label: "Active",
          value: ACTIVE_STATUSES.join(",")
        }
      ]
    )
  }, [project_status])

  const activeFiltersCount = useMemo(
    () => countActiveFilters(filters, filterFields),
    [filters, filterFields]
  )

  if (fetching) return <Skeleton.Node className={"w-full h-8"} />

  const hasSortOptions = sortOptions.length > 0
  const hasFilterFields = filterFields.length > 0

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={"small"}>
        <Radio.Group
          value={filters?.project_status}
          onChange={(e) => onFilterChange("project_status")(e.target.value)}
          buttonStyle="solid"
          optionType={"button"}
          options={statusOptions}
        />

        <Flex gap={8}>
          {hasFilterFields ? (
            <Badge count={activeFiltersCount} size="small">
              <Button
                type={activeFiltersCount ? "primary" : "default"}
                icon={<FilterOutlined />}
                onClick={() => setDrawerOpen(true)}
              >
                Filter
              </Button>
            </Badge>
          ) : (
            <Button disabled icon={<FilterOutlined />}>
              Filter
            </Button>
          )}

          {hasSortOptions ? (
            <ProjectListGridFiltersOrdering
              ordering={filters?.ordering || null}
              sortOptions={sortOptions}
              onOrderingChange={onFilterChange("ordering")}
            />
          ) : (
            <Button disabled icon={<SortAscendingOutlined />}>
              Sort
            </Button>
          )}
        </Flex>
      </Flex>

      {hasFilterFields && (
        <ProjectListGridFiltersDrawer
          open={drawerOpen}
          filters={filters}
          filterOptions={filterOptions}
          filterFields={filterFields}
          onClose={() => setDrawerOpen(false)}
          onFiltersChange={onFiltersChange}
        />
      )}
    </>
  )
}
